import {
  type AchievementCategory,
  SEASON_ROLES,
  attendancePercent,
  dayMonth,
  seasonStory,
  useAlbumPhotos,
  useAlbums,
  useYearBook,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { DownloadSimpleIcon } from '@phosphor-icons/react/DownloadSimple';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import {
  Block,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  grad,
  pillClass,
} from '../design/forms';
import { KindArt } from '../pages/AchievementsPage';
import { EventArt, FaceAvatar, useFace } from './home/LineArt';
import { Label, LifeArt, Sub } from './LifeArt';

/*
 * «Альбом року» in «Форми 2» (28.10). The title on the paper with «Зберегти PDF»; the cover — the
 * student's face and name on a soft lavender card, the class's photos beside; then four pages as white
 * blocks two by two (no empty half-row): the year in numbers, the best works (the grade by hand, as a
 * teacher's mark, the subject's picture), the achievements and the clubs, the class season and the
 * letters. No stickers, no squared paper, no emoji: our pictures in one line.
 */

/** the colour of a grade's level: low coral, then butter, sky, lime */
const levelTone = (points: number): Grad =>
  points <= 3 ? 'coral' : points <= 6 ? 'butter' : points <= 9 ? 'sky' : 'lime';

const decimal = (n: number) => String(n).replace('.', ',');

function Page({
  n,
  total,
  title,
  children,
}: {
  n: number;
  total: number;
  title?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Block className="relative min-h-[360px] pb-12 break-inside-avoid">
      {title && <Head title={title} />}
      {children}
      <span className="absolute right-6 bottom-5 text-[12px]" style={{ color: INK_FAINT }}>
        {t('yearbook.page', { n, total })}
      </span>
    </Block>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-[20px] p-4" style={{ backgroundColor: PAPER }}>
      <p
        className="font-display text-[38px] leading-[42px] font-light tracking-[-0.04em]"
        style={{ color: INK }}
      >
        {value}
      </p>
      <p className="mt-0.5 text-[13px]" style={{ color: INK_SOFT }}>
        {label}
      </p>
    </div>
  );
}

function ClassPhotos({ classId }: { classId: string }) {
  const albums = useAlbums(classId).data ?? [];
  const photos = useAlbumPhotos(albums[0]?.id).data ?? [];
  const second = useAlbumPhotos(albums[1]?.id).data ?? [];
  const all = [...photos, ...second].filter((p) => p.url).slice(0, 6);
  if (all.length === 0) return null;
  // as many in a row as there are, up to four; five and six — three by two (no empty place)
  const cols =
    ['grid-cols-2', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4'][all.length - 1] ?? 'grid-cols-3';
  return (
    <div className={`grid gap-2 ${cols}`}>
      {all.map((p) => (
        <img
          key={p.id}
          src={p.url as string}
          alt={p.caption ?? ''}
          className="aspect-square w-full rounded-[16px] bg-white object-cover"
        />
      ))}
    </div>
  );
}

/**
 * «Альбом року»: a student's school year gathered by itself — the cover, the year in numbers, the
 * best works, achievements and clubs, the class's season and its photos. «Зберегти PDF» prints it.
 */
export function YearBookPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const book = useYearBook(child?.memberId ?? '');
  const [face] = useFace(child?.memberId ?? '');
  const head = (
    <PageHead title={t('yearbook.title')} note={book.data?.year} sub={t('yearbook.building')}>
      {book.data && (
        <button type="button" onClick={() => window.print()} className={pillClass('dark')}>
          {t('yearbook.save')}
          <PillIcon icon={DownloadSimpleIcon} />
        </button>
      )}
    </PageHead>
  );
  if (!child || book.isPending || !book.data)
    return (
      <div className="flex flex-col gap-4">
        {head}
        {book.isPending && child ? (
          <div className="h-[320px] animate-pulse rounded-[28px] bg-white" aria-busy />
        ) : (
          <Block>
            <div className="flex flex-col items-center py-10 text-center">
              <EventArt kind="celebration" tone="lavender" size={88} />
              <p className="mt-3 text-[16px]" style={{ color: INK_SOFT }}>
                {t('yearbook.building')}
              </p>
            </div>
          </Block>
        )}
      </div>
    );
  const b = book.data;
  const story = b.season ? seasonStory(b.season.key) : undefined;
  const role = SEASON_ROLES.find((r) => r.key === b.season?.role);
  const total = 5;
  const initials = `${b.firstName[0] ?? ''}${b.lastName[0] ?? ''}`.toUpperCase();
  const passed = b.season ? (b.season.finished ? b.season.stages : b.season.stage) : 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="print:hidden">{head}</div>

      <div className="print-area grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* the cover: the face and the name on lavender, the class's photos beside */}
        <section
          className="relative isolate grid min-w-0 grid-cols-1 items-center gap-6 overflow-hidden rounded-[28px] p-6 pb-12 break-inside-avoid md:p-8 md:pb-12 lg:col-span-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
          style={{ backgroundImage: grad('lavender'), color: GRAD.lavender.ink }}
        >
          <EventArt
            kind="celebration"
            tone="lavender"
            size={220}
            ghost
            className="pointer-events-none absolute -bottom-10 left-[38%] -z-10 hidden lg:block"
          />
          <div className="flex min-w-0 flex-col">
            <FaceAvatar face={face} initials={initials} size={104} />
            <p className="mt-5 text-[14px] font-medium">{t('yearbook.hand', { year: b.year })}</p>
            <p
              className="font-display text-[44px] leading-[50px] font-light tracking-[-0.045em] md:text-[56px] md:leading-[62px]"
              style={{ color: INK }}
            >
              {b.firstName} {b.lastName}
            </p>
            <p className="mt-1 text-[16px]">
              {t('yearbook.cover', { class: b.classLabel, year: b.year })}
            </p>
          </div>
          {child.class && <ClassPhotos classId={child.class.id} />}
          <span className="absolute right-6 bottom-5 text-[12px] md:right-8">
            {t('yearbook.page', { n: 1, total })}
          </span>
        </section>

        <Page n={2} total={total} title={t('yearbook.numbers')}>
          <div className="grid grid-cols-2 gap-2">
            <Stat value={String(b.lessons)} label={t('yearbook.lessons')} />
            <Stat value={`${attendancePercent(b)}%`} label={t('yearbook.attendance')} />
            <Stat
              value={b.average === null ? '—' : decimal(b.average)}
              label={t('yearbook.average')}
            />
            <Stat value={String(b.homework)} label={t('yearbook.homework')} />
            <Stat
              value={b.quizzes.count > 0 ? `${b.quizzes.count} · ${b.quizzes.percent ?? 0}%` : '0'}
              label={t('yearbook.quizzes')}
            />
            <Stat value={String(b.puzzles.right)} label={t('yearbook.puzzles')} />
          </div>
        </Page>

        <Page n={3} total={total} title={t('yearbook.best')}>
          {b.best.length === 0 ? (
            <p style={{ color: INK_SOFT }}>{t('yearbook.noBest')}</p>
          ) : (
            <ul className="flex flex-col">
              {b.best.map((x) => {
                const look = subjectLook(x.subject);
                return (
                  <li
                    key={`${x.subject}-${x.graded_on}-${x.points}`}
                    className="flex items-center gap-3 py-2"
                    style={{ borderTop: `1px solid ${LINE}` }}
                  >
                    <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={34} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px]" style={{ color: INK }}>
                        {x.subject}
                        {x.title && <span style={{ color: INK_SOFT }}> · {x.title}</span>}
                      </span>
                      <span className="block text-[12px]" style={{ color: INK_FAINT }}>
                        {dayMonth(x.graded_on)}
                      </span>
                    </span>
                    {/* the grade as a small mark of its level */}
                    <span
                      className="inline-flex h-9 min-w-9 shrink-0 items-center justify-center rounded-[10px] px-1.5 text-[16px] leading-none font-semibold tabular-nums"
                      style={{ backgroundImage: grad(levelTone(x.points)), color: INK }}
                    >
                      {x.points}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {b.subjects.length > 0 && (
            <div className="mt-5">
              <Sub>{t('yearbook.subjects')}</Sub>
              <p className="flex flex-wrap gap-1.5">
                {b.subjects.slice(0, 3).map((s) => {
                  const look = subjectLook(s.subject);
                  return (
                    <span
                      key={s.subject}
                      className="inline-flex h-10 items-center gap-2 rounded-[12px] pr-3 pl-1.5 text-[14px]"
                      style={{ backgroundColor: PAPER, color: INK }}
                    >
                      <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={28} />
                      {s.subject}
                      <span style={{ color: INK_FAINT }}>{decimal(s.average)}</span>
                    </span>
                  );
                })}
              </p>
            </div>
          )}
        </Page>

        <Page n={4} total={total} title={t('yearbook.achievements')}>
          {b.achievements.length === 0 ? (
            <p style={{ color: INK_SOFT }}>{t('yearbook.noAchievements')}</p>
          ) : (
            <ul className="flex flex-col">
              {b.achievements.map((a) => (
                <li
                  key={`${a.title}-${a.on}`}
                  className="flex items-center gap-3 py-2"
                  style={{ borderTop: `1px solid ${LINE}` }}
                >
                  <KindArt category={a.category as AchievementCategory} size={34} />
                  <span className="min-w-0 flex-1 text-[15px]" style={{ color: INK }}>
                    {a.title}
                    <span className="block text-[12px]" style={{ color: INK_FAINT }}>
                      {dayMonth(a.on)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {b.clubs.length > 0 && (
            <div className="mt-5">
              <Sub>{t('yearbook.clubs')}</Sub>
              <p className="flex flex-wrap gap-1.5">
                {b.clubs.map((c, i) => (
                  <Label
                    key={c.title}
                    tone={(['mint', 'lavender', 'butter', 'pink'] as const)[i % 4]}
                  >
                    {c.title}
                  </Label>
                ))}
              </p>
            </div>
          )}
        </Page>

        <Page n={5} total={total} title={t('yearbook.season')}>
          {story && b.season ? (
            <div className="flex items-start gap-4">
              <LifeArt kind="rocket" tone="lavender" size={64} />
              <div className="min-w-0">
                <p className="text-[15px] leading-[22px]" style={{ color: INK }}>
                  {t('yearbook.seasonLine', {
                    title: story.title,
                    stage: passed,
                    stages: b.season.stages,
                    role: role?.name ?? '',
                  })}
                </p>
                {/* the stations: the passed ones lime */}
                <p className="mt-3 flex flex-wrap gap-1.5" aria-hidden>
                  {story.stations.map((s, i) => (
                    <span
                      key={s.title}
                      title={s.title}
                      className="size-3.5 rounded-full"
                      style={
                        i < passed ? { backgroundImage: grad('lime') } : { backgroundColor: PAPER }
                      }
                    />
                  ))}
                </p>
              </div>
            </div>
          ) : null}
          {b.letters > 0 && (
            <div
              className={`flex items-center gap-4 ${story && b.season ? 'mt-6 border-t pt-5' : ''}`}
              style={{ borderColor: LINE }}
            >
              <LifeArt kind="letter" tone="butter" size={56} />
              <p className="text-[15px] leading-[22px]" style={{ color: INK }}>
                {t('yearbook.letters', { count: b.letters })}
              </p>
            </div>
          )}
        </Page>
      </div>
    </div>
  );
}
